/**
 * TheLatest — "Chronological" (Bento bottom-left quadrant)
 *
 * Strict reverse-chronological list of the 3 most recent posts.
 * No infinite scroll. "View Archive" button to switch to full feed.
 */

import { useMemo } from 'react';
import type { ClubTheme } from './themes';
import type { SocialPost } from './types';
import { useSocialFeed } from './useSocialFeed';
import PostCard from './PostCard';
import SystemFlairPost from './SystemFlairPost';

interface TheLatestProps {
  clubId: string;
  theme: ClubTheme;
  onViewArchive?: () => void;
  onRefresh?: () => void;
  onQuote?: (post: SocialPost) => void;
  onNavigateToPost?: (postId: string) => void;
}

export default function TheLatest({
  clubId,
  theme,
  onViewArchive,
  onRefresh,
  onQuote,
  onNavigateToPost,
}: TheLatestProps) {
  const { posts, loading } = useSocialFeed(clubId);

  // Take only the 3 most recent
  const latest = useMemo(() => posts.slice(0, 3), [posts]);

  return (
    <div
      className="flex h-full flex-col rounded-2xl p-4"
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.muted}15`,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.colors.text }}>
          The Latest
        </span>
        <span className="text-[10px]" style={{ color: theme.colors.muted }}>
          {posts.length} total
        </span>
      </div>

      {/* Loading */}
      {loading && latest.length === 0 && (
        <div className="flex flex-1 items-center justify-center">
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
            style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
          />
        </div>
      )}

      {/* Posts */}
      <div className="flex-1 space-y-2 overflow-y-auto">
        {latest.map((post) =>
          post.author_type === 'system' && post.post_type !== 'post' ? (
            <SystemFlairPost key={post.id} post={post} theme={theme} />
          ) : (
            <LatestPostMini
              key={post.id}
              post={post}
              theme={theme}
              onClick={() => onNavigateToPost?.(post.id)}
            />
          ),
        )}
      </div>

      {/* Empty state */}
      {!loading && latest.length === 0 && (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-xs" style={{ color: theme.colors.muted }}>
            No posts yet
          </p>
        </div>
      )}

      {/* View Archive button */}
      {onViewArchive && (
        <button
          type="button"
          onClick={onViewArchive}
          className="mt-3 w-full rounded-xl py-2 text-[11px] font-medium transition-colors"
          style={{
            backgroundColor: `${theme.colors.primary}12`,
            color: theme.colors.primary,
          }}
        >
          View Archive
        </button>
      )}
    </div>
  );
}

// ─── Compact post card for the latest feed ───────────────────────

interface LatestPostMiniProps {
  post: SocialPost;
  theme: ClubTheme;
  onClick?: () => void;
}

function LatestPostMini({ post, theme, onClick }: LatestPostMiniProps) {
  const authorName = post.author?.full_name || 'Unknown';
  const avatarUrl = post.author?.avatar_url;
  const bodyPreview = (post.body || '').slice(0, 100);
  const timeAgo = formatTimeAgo(post.created_at);
  const totalReactions = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);

  return (
    <div
      className="rounded-xl p-3 transition-colors hover:opacity-90 cursor-pointer"
      style={{
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.muted}10`,
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Author row */}
      <div className="flex items-center gap-2 mb-1.5">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
        ) : (
          <div
            className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold"
            style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
          >
            {(authorName[0] || '?').toUpperCase()}
          </div>
        )}
        <span className="text-[11px] font-semibold truncate flex-1" style={{ color: theme.colors.text }}>
          {authorName}
        </span>
        <span className="text-[9px] shrink-0" style={{ color: theme.colors.muted }}>
          {timeAgo}
        </span>
      </div>

      {/* Body */}
      <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: theme.colors.muted }}>
        {bodyPreview}{bodyPreview.length >= 100 ? '...' : ''}
      </p>

      {/* Stats */}
      {(totalReactions > 0 || post.comment_count > 0) && (
        <div className="mt-1.5 flex gap-3 text-[9px]" style={{ color: theme.colors.muted }}>
          {totalReactions > 0 && <span>{totalReactions} reactions</span>}
          {post.comment_count > 0 && <span>{post.comment_count} comments</span>}
        </div>
      )}
    </div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60_000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
